import React, { type ReactElement, useCallback, useMemo, useState } from "react";
import { RelayEnvironmentProvider } from "react-relay";

import { faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { NoteBlockStatus } from "~/actionItems/enum";
import Checkbox from "~/common/NoteCheckbox";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import Avatar, { avatarUserQuery, type UserAttrs } from "~/ui/legacy/Avatar";
import Menu from "~/ui/menu";

import type { AvatarUserQuery } from "~/__generated__/AvatarUserQuery.graphql";

import styles from "./ActionItemCompletionMenu.module.css";

export type AssigneeCompletionData = {
	id: string;
	status: string;
	attendeeAttrs?: UserAttrs;
};

const assigneeStatusToCheckboxState: { [key: string]: "checked" | "unchecked" | "indeterminate" } = {
	[NoteBlockStatus.DONE]: "checked",
	[NoteBlockStatus.NOT_DONE]: "unchecked",
	[NoteBlockStatus.WONT_DO]: "indeterminate",
};

interface AssigneeProps {
	assignee: AssigneeCompletionData;
}

function Assignee({ assignee }: AssigneeProps) {
	if (!assignee.attendeeAttrs) return null;
	return (
		<>
			<Avatar user={assignee.attendeeAttrs} width="1.5em" className={styles.avatar} />
			<span className={styles.name}>{assignee.attendeeAttrs.fullName}</span>
		</>
	);
}

function LazyAssignee({ assignee }: AssigneeProps) {
	const { node: user } = useLazyLoadQuery<AvatarUserQuery>(
		avatarUserQuery,
		{ userId: assignee.id },
		{ fetchPolicy: "store-or-network" },
	);
	if (user?.__typename !== "User") return null;
	const assigneeWithUserData = {
		...assignee,
		attendeeAttrs: user,
	};
	return <Assignee assignee={assigneeWithUserData} />;
}

interface ActionItemMenuItemProps {
	assignee: AssigneeCompletionData;
	onToggle: (assignee: AssigneeCompletionData) => void;
}

function ActionItemMenuItem({ assignee, onToggle }: ActionItemMenuItemProps) {
	const onToggleItem = useCallback(
		(event: Event | React.MouseEvent<HTMLInputElement, MouseEvent>) => {
			event.preventDefault();
			onToggle(assignee);
		},
		[assignee, onToggle],
	);

	return (
		<Menu.Item className={styles.item} onSelect={onToggleItem}>
			<Checkbox
				type="action-item"
				state={assigneeStatusToCheckboxState[assignee.status]}
				className={styles.checkbox}
				readOnly
				checkboxSize="large"
				onMouseDown={onToggleItem}
			/>
			{assignee.attendeeAttrs ? (
				<Assignee assignee={assignee} />
			) : (
				<React.Suspense fallback={<></>}>
					<LazyAssignee assignee={assignee} />
				</React.Suspense>
			)}
		</Menu.Item>
	);
}

interface ActionItemCompletionMenuProps {
	trigger: ReactElement;
	assignees: Array<AssigneeCompletionData>;
	showAssignYourself?: boolean;
	disabled?: boolean;
	onToggle: (assignee: AssigneeCompletionData) => void;
	onAssignYourself?: () => void;
	overrideOpening?: () => void;
}

export default function ActionItemCompletionMenu({
	trigger,
	assignees,
	showAssignYourself,
	disabled,
	onToggle,
	onAssignYourself,
	overrideOpening,
}: ActionItemCompletionMenuProps) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const toggleOpen = useCallback(() => {
		if (overrideOpening) {
			overrideOpening();
			return;
		}
		setIsOpen(isOpen => !isOpen);
	}, [overrideOpening]);
	const onClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		event.preventDefault();
	}, []);
	const onClickAssignYourself = useCallback(() => {
		setIsOpen(false);
		onAssignYourself?.();
	}, [onAssignYourself]);

	const sortedAssignees = useMemo(() => {
		return assignees.sort((a, b) => {
			const displayNameA = a.attendeeAttrs?.fullName || a.attendeeAttrs?.email || "";
			return displayNameA.localeCompare(b.attendeeAttrs?.fullName || b.attendeeAttrs?.email || "");
		});
	}, [assignees]);

	return (
		<Menu open={isOpen} onOpenChange={toggleOpen}>
			<Menu.Trigger onClick={onClick} disabled={disabled}>
				{trigger}
			</Menu.Trigger>
			<Menu.Portal>
				<Menu.Content>
					<RelayEnvironmentProvider environment={environment}>
						<Menu.Label>Completion</Menu.Label>
						{sortedAssignees.map(assignee => (
							<ActionItemMenuItem key={assignee.id} assignee={assignee} onToggle={onToggle} />
						))}
						{showAssignYourself && onAssignYourself && (
							<>
								<Menu.Separator />
								<Menu.Item onSelect={onClickAssignYourself} className={styles.assignYourself}>
									<FontAwesomeIcon icon={faUserPlus} />
									Add yourself as an assignee
								</Menu.Item>
							</>
						)}
					</RelayEnvironmentProvider>
				</Menu.Content>
			</Menu.Portal>
		</Menu>
	);
}
